import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { isArray } from 'lodash';
import { NefcoDateHelper } from 'src/app/shared/helpers/nefco-date.class';
import { IApiContactFilterType, IApiSageInvoiceFilter, IApiSageInvoiceFilterType } from 'src/app/shared/modules/graphql/types/types';

@Component({
  selector: 'app-bookkeeping-sage-invoice-filters',
  templateUrl: './bookkeeping-sage-invoice-filters.component.html',
  styleUrls: ['./bookkeeping-sage-invoice-filters.component.scss']
})
export class BookkeepingSageInvoiceFiltersComponent {
  private _filters: IApiSageInvoiceFilter[] = [];
  public filterTypes = IApiSageInvoiceFilterType;

  public get filters(): IApiSageInvoiceFilter[] {
    return this._filters;
  }
  public set filters(val: IApiSageInvoiceFilter[]) {
    this._filters = val;
  }

  @ViewChild("startDateInput") public startDateInput: any;
  @ViewChild("endDateInput") public endDateInput: any;
  @ViewChild('startDateRange', { static: false }) public startDateRange: any;
  @ViewChild('endDateRange', { static: false }) public endDateRange: any;

  @ViewChild("dueStartDateInput") public dueStartDateInput: any;
  @ViewChild("dueEndDateInput") public dueEndDateInput: any;
  @ViewChild('dueStartDateRange', { static: false }) public dueStartDateRange: any;
  @ViewChild('dueEndDateRange', { static: false }) public dueEndDateRange: any;

  @Output() public clearFilter = new EventEmitter<IApiSageInvoiceFilter[]>();
  @Output() public applyFilter = new EventEmitter<IApiSageInvoiceFilter[]>();

  public invoiceDateRange: { startDate: Date | null; endDate: Date | null } = { startDate: null, endDate: null };
  public dueDatePickerRange: { startDate: Date | null; endDate: Date | null } = { startDate: null, endDate: null };
  public agingRange: { start: number | null; end: number | null } = { start: null, end: null };

  public sageContactFilters = [
    {
      type: IApiContactFilterType.SageContact,
      value: "true"
    },
    {
      type: IApiContactFilterType.ViewUnpaidInvoiceFilter,
      value: "true"
    }
  ];

  public filterByIdValue(filter: IApiSageInvoiceFilterType): any {
    const value = this.filters.find(({ type }) => type === filter)?.value;
    return value ? JSON.parse(value) : null;
  }

  public setFilters(
    value: string | number | any[] | undefined | null,
    type: IApiSageInvoiceFilterType
  ): void {
    const hasValue = (val: any) => val !== undefined && val !== null && val !== "";
    const filtersCopy = this.filters.filter(f => f.type !== type);

    this.filters = hasValue(value)
      ? [
        ...filtersCopy,
        {
          type,
          value: Array.isArray(value)
            ? value.length > 0
              ? JSON.stringify(value)
              : null
            : String(value)
        }
      ]
      : filtersCopy;
  }

  public filterValue(filter: IApiSageInvoiceFilterType): any {
    const value = this.filters.find(({ type }) => type === filter)?.value;
    if (value === undefined || value === null) return null;

    if (filter === IApiSageInvoiceFilterType.TotalAmountDue) {
      return Number(value);
    }

    if (filter === IApiSageInvoiceFilterType.DueInDays || value === "true" || value === "false" || (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{')))) {
      try {
        return JSON.parse(value as string);
      } catch {
        return value;
      }
    }

    return value || null;
  }

  public appliedFilters(): void {
    this.applyFilter.emit(this.filters);
  }

  public clearAll(): void {
    this.filters = [];
    this.invoiceDateRange = { startDate: null, endDate: null };
    this.dueDatePickerRange = { startDate: null, endDate: null };
    this.agingRange = { start: null, end: null };
    this.clearFilter.emit(this.filters);
  }

  public setVal(event: any, type: IApiSageInvoiceFilterType): void {
    const val = event?.length > 0 ? JSON.stringify(event) : null;
    this.setFilters(val, type);
  }

  public updateDateFilters(): void {
    this._updateDateRange(
      this.startDateInput?.value,
      this.endDateInput?.value,
      IApiSageInvoiceFilterType.InvoiceDate,
      'invoiceDateRange'
    );
  }

  public updateDueDateFilters(): void {
    this._updateDateRange(
      this.dueStartDateInput?.value,
      this.dueEndDateInput?.value,
      IApiSageInvoiceFilterType.DueDate,
      'dueDatePickerRange'
    );
  }

  private _updateDateRange(
    start: Date | null,
    end: Date | null,
    filterType: IApiSageInvoiceFilterType,
    rangeKey: 'invoiceDateRange' | 'dueDatePickerRange'
  ): void {
    this[rangeKey] = { startDate: start, endDate: end };
    if (start && end) {
      this.setFilters(
        JSON.stringify({
          startDate: NefcoDateHelper.toUtcStartOfDay(start, true),
          endDate: NefcoDateHelper.toUtcStartOfDay(end, false),
        }),
        filterType
      );
    } else {
      this.setFilters(null, filterType);
    }
  }

  public updateAgingFilters(): void {
    if (this.agingRange.start !== null && this.agingRange.end !== null) {
      this.setFilters(JSON.stringify(this.agingRange), IApiSageInvoiceFilterType.DueInDays);
    } else {
      this.setFilters(null, IApiSageInvoiceFilterType.DueInDays);
    }
  }

  public get isFormValid(): boolean {
    const { start, end } = this.agingRange;
    return !((start !== null && end === null) || (start === null && end !== null));
  }

  public focusDateInput(start = true): void {
    this._focusInput(start ? this.startDateRange : this.endDateRange);
  }

  public focusDueDateInput(start = true): void {
    this._focusInput(start ? this.dueStartDateRange : this.dueEndDateRange);
  }

  private _focusInput(rangeComponent: any): void {
    if (rangeComponent) {
      setTimeout(() => rangeComponent.focus(), 1);
    }
  }
}
